import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, FileText, ImagePlus, ListChecks, Settings2, Save, LockKeyhole, Loader2, Upload } from "lucide-react";
import QuestionImage from "./components/QuestionImage";
import { useParams, useNavigate } from "react-router-dom";
import { db, storage } from "./firebaseConfig";
import {
  doc,
  getDoc,
  updateDoc,
  deleteField,
  Timestamp
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import MultipleChoiceEditor from "./components/MultipleChoiceEditor";
import {
  QUESTION_TYPE_MULTIPLE_CHOICE,
  QUESTION_TYPE_SHORT_ANSWER,
  buildManualQuestionFields,
  createDefaultMcqOptions,
  getEffectiveQuestionType,
  prepareMcqOptionsForEditing,
  validateMcqQuestion,
} from "./utils/mcqQuestion";
import "./EditQuestion.css";

const EditQuestion = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const submittingRef = useRef(false);
  const [question, setQuestion] = useState({
    questionType: QUESTION_TYPE_SHORT_ANSWER,
    questionText: "",
    markSchemeAnswer: "",
    maxMark: "",
    topic: "",
    subject: "",
    level: "",
    specCode: "",
    textBoxSize: "medium",
    options: createDefaultMcqOptions(),
    correctOptionId: "",
  });
  const [mcqErrors, setMcqErrors] = useState([]);

  const [questionImage, setQuestionImage] = useState(null);
  const [questionImageURL, setQuestionImageURL] = useState("");
  const [originalQuestionImageURL, setOriginalQuestionImageURL] = useState("");
  const [canonicalMedia, setCanonicalMedia] = useState(null);

  // Fetch question data on component mount
  useEffect(() => {
    const fetchQuestion = async () => {
      try {
        console.log("Fetching question with ID:", questionId);
        const docRef = doc(db, "questions", questionId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log("Question data loaded:", data);
          const questionType = getEffectiveQuestionType(data.questionType);
          
          setQuestion({
            questionType,
            questionText: data.questionText || "",
            markSchemeAnswer: data.markSchemeAnswer || "",
            maxMark: questionType === QUESTION_TYPE_MULTIPLE_CHOICE ? 1 : (data.maxMark || ""),
            topic: data.topic || "",
            subject: data.subject || "",
            level: data.level || "",
            specCode: data.specCode || "",
            textBoxSize: data.textBoxSize || "medium",
            options: questionType === QUESTION_TYPE_MULTIPLE_CHOICE
              ? prepareMcqOptionsForEditing(data.options)
              : createDefaultMcqOptions(),
            correctOptionId: questionType === QUESTION_TYPE_MULTIPLE_CHOICE
              ? (data.correctOptionId || "")
              : "",
          });
          
          // Set image URLs if they exist
          if (data.questionImageURL) {
            setQuestionImageURL(data.questionImageURL);
            setOriginalQuestionImageURL(data.questionImageURL);
          }
          if (data.questionMedia && !data.questionImageURL) setCanonicalMedia(data.questionMedia);
        } else {
          console.log("No such document!");
          showToast("Question not found", "error");
          navigate(-1); // Go back to previous page
        }
      } catch (error) {
        console.error("Error fetching question: ", error.message);
        showToast("Error loading question", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuestion();
  }, [questionId, navigate]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setQuestion((prev) => ({ ...prev, [name]: value }));
  };

  // Handle file selection for question image
  const handleQuestionFileChange = (e) => {
    if (e.target.files[0]) {
      setQuestionImage(e.target.files[0]);
    }
  };

  // Upload question image
  const uploadQuestionImage = async () => {
    if (!questionImage) return;
    
    try {
      const storageRef = ref(
        storage,
        `questions/${Date.now()}_${questionImage.name}`
      );
      await uploadBytes(storageRef, questionImage);
      const url = await getDownloadURL(storageRef);
      setQuestionImageURL(url);
      showToast("Question image uploaded successfully", "success");
    } catch (error) {
      console.error("Error uploading question image:", error.message);
      showToast("Error uploading question image", "error");
    }
  };

  // Show toast notification
  const showToast = (message, type = "success") => {
    const toast = document.createElement("div");
    toast.className = `eq-toast eq-toast--${type}`;
    toast.setAttribute("role", type === "error" ? "alert" : "status");
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => document.body.removeChild(toast), 3000);
  };

  // Handle form submission. One submission saves once: the ref blocks repeat
  // clicks/Enter before React re-renders, and stays held after a successful save
  // until navigation, so the page cannot be saved (and announced) twice.
  const releaseSave = () => {
    submittingRef.current = false;
    setIsSaving(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSaving(true);

    // Validate required fields
    if (
      !question.maxMark ||
      !question.topic ||
      !question.subject ||
      !question.level ||
      !question.specCode
    ) {
      showToast("Max Mark, Topic, Subject, Level, and Spec Code are required!", "error");
      releaseSave();
      return;
    }

    const isMultipleChoice = question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE;

    // MCQs require text; written questions retain the existing text-or-image behaviour.
    if (
      (isMultipleChoice && !question.questionText.trim()) ||
      (!isMultipleChoice && !question.questionText && !questionImageURL && !questionImage && !canonicalMedia)
    ) {
      showToast(
        isMultipleChoice
          ? "Please provide question text for the multiple-choice question."
          : "Please provide either a question text or upload a question image.",
        "error"
      );
      releaseSave();
      return;
    }

    if (isMultipleChoice) {
      const validation = validateMcqQuestion(question);
      if (!validation.isValid) {
        setMcqErrors(validation.errors);
        releaseSave();
        return;
      }
      setMcqErrors([]);
    } else if (!question.markSchemeAnswer) {
      showToast("Please provide a mark scheme answer.", "error");
      releaseSave();
      return;
    }

    try {
      const questionData = {
        ...buildManualQuestionFields(question),
        updatedAt: Timestamp.now(),
      };
      
      // Handle question image - if there's a new image selected but not uploaded yet
      if (questionImage) {
        try {
          // Upload the new question image
          const storageRef = ref(
            storage,
            `questions/${Date.now()}_${questionImage.name}`
          );
          await uploadBytes(storageRef, questionImage);
          const url = await getDownloadURL(storageRef);
          questionData.questionImageURL = url;
          if (canonicalMedia) questionData.questionMedia = deleteField();
          // Update the state for future reference
          setQuestionImageURL(url);
          setOriginalQuestionImageURL(url);
          setCanonicalMedia(null);
          showToast("Question image uploaded successfully", "success");
        } catch (error) {
          console.error("Error uploading question image:", error.message);
          showToast("Error uploading question image", "error");
          releaseSave();
          return;
        }
      } else if (questionImageURL) {
        // Use the already uploaded image URL
        questionData.questionImageURL = questionImageURL;
        if (canonicalMedia) questionData.questionMedia = deleteField();
      } else if (originalQuestionImageURL) {
        // Keep the original image URL if no new image was uploaded
        questionData.questionImageURL = originalQuestionImageURL;
      }
      
      console.log("Updating question with data:", questionData);
      
      // Update the document in Firestore
      const questionRef = doc(db, "questions", questionId);
      await updateDoc(questionRef, questionData);
      
      console.log("Update successful");
      setIsSaved(true);
      showToast("Question updated successfully!", "success");

      // Navigate back to the question manager
      setTimeout(() => {
        navigate(-1); // Go back to previous page
      }, 1500);
    } catch (error) {
      console.error("Error updating question: ", error.message);
      submittingRef.current = false;
      showToast("Error updating question. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <main className="edit-question-container edit-question-page eq-loading-page">
        <div className="eq-loading-panel" role="status">
          <Loader2 className="eq-spinner" size={32} aria-hidden="true" />
          <h1>Loading question...</h1>
          <p>Getting your question ready to edit.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="edit-question-container edit-question-page" aria-labelledby="edit-question-title">
      <div className="eq-shell">
        <button
          type="button"
          className="eq-back-button"
          onClick={() => navigate(-1)} // Go back to previous page
        >
          <ArrowLeft size={18} aria-hidden="true" /> Back to Question Manager
        </button>
        <header className="eq-page-header">
          <div>
            <p className="eq-eyebrow">Question bank</p>
            <h1 id="edit-question-title">Edit Question</h1>
            <p className="eq-page-intro">Refine your question, review the answer settings and keep the details up to date.</p>
          </div>
          <span className="eq-type-badge"><FileText size={16} aria-hidden="true" />{question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE ? "Multiple Choice" : "Short Answer"}</span>
        </header>

        <form className="eq-form" onSubmit={handleSubmit} aria-busy={isSaving}>
          <section className="eq-section" aria-labelledby="eq-content-title">
            <div className="eq-section-heading">
              <span className="eq-section-icon"><FileText size={22} aria-hidden="true" /></span>
              <div><h2 id="eq-content-title">Question content</h2><p>Set out what you want your students to answer.</p></div>
            </div>
          <div className="eq-type-control">
            <label className="eq-label" htmlFor="edit-question-type">Question Type <LockKeyhole size={13} aria-hidden="true" /></label>
            <select
              id="edit-question-type"
              className="eq-input"
              value={question.questionType}
              disabled
              aria-describedby="eq-type-note"
            >
              <option value={QUESTION_TYPE_SHORT_ANSWER}>Short Answer</option>
              <option value={QUESTION_TYPE_MULTIPLE_CHOICE}>Multiple Choice</option>
            </select>
            <p className="eq-help" id="eq-type-note">
              The type of a saved question is locked to prevent accidental answer-data loss.
            </p>
          </div>

            <div className="eq-field">
              <label className="eq-label" htmlFor="eq-question-text">
                Question Text{question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE ? " *" : ""}
              </label>
              <textarea
                id="eq-question-text"
                name="questionText"
                placeholder={question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE
                  ? "Enter question text here"
                  : "Enter question text here (Optional if image uploaded)"}
                value={question.questionText}
                onChange={handleInputChange}
                className="eq-input eq-textarea"
                rows={8}
                aria-describedby="eq-question-help"
                aria-required={question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE}
              />
              <p className="eq-help" id="eq-question-help">{question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE ? "Question text is required for multiple-choice questions." : "Use question text, an image, or both. Line breaks and indentation are kept as entered."}</p>
            </div>
          </section>

          <section className="eq-section" aria-labelledby="eq-answer-title">
            <div className="eq-section-heading">
              <span className="eq-section-icon eq-section-icon--purple"><ListChecks size={22} aria-hidden="true" /></span>
              <div><h2 id="eq-answer-title">{question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE ? "Answer options" : "Answer & mark scheme"}</h2><p>{question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE ? "Edit the choices and select the correct answer." : "Give a clear reference answer and marking guidance."}</p></div>
            </div>
            {question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE ? (
              <MultipleChoiceEditor
                options={question.options}
                correctOptionId={question.correctOptionId}
                onOptionsChange={(options) => {
                  setQuestion((previous) => ({ ...previous, options }));
                  setMcqErrors([]);
                }}
                onCorrectOptionIdChange={(correctOptionId) => {
                  setQuestion((previous) => ({ ...previous, correctOptionId }));
                  setMcqErrors([]);
                }}
                errors={mcqErrors}
                idPrefix="edit-mcq"
              />
            ) : (
              <div>
                <label className="eq-label" htmlFor="eq-mark-scheme">
                  Mark Scheme Answer *
                </label>
                <textarea
                  id="eq-mark-scheme"
                  name="markSchemeAnswer"
                  placeholder="Enter mark scheme answer here"
                  value={question.markSchemeAnswer}
                  onChange={handleInputChange}
                  className="eq-input eq-textarea eq-textarea--scheme"
                  rows={10}
                  aria-describedby="eq-scheme-help"
                  aria-required="true"
                />
                <p className="eq-help" id="eq-scheme-help">Include the accepted answer, marking points or code. Line breaks and indentation are kept as entered.</p>
              </div>
            )}
          </section>

          <section className="eq-section" aria-labelledby="eq-image-title">
            <div className="eq-section-heading">
              <span className="eq-section-icon"><ImagePlus size={22} aria-hidden="true" /></span>
              <div><h2 id="eq-image-title">Question image <span className="eq-optional">Optional</span></h2><p>Add a diagram, source or image to accompany the question.</p></div>
            </div>
            <div className="eq-media-grid">
              {(originalQuestionImageURL || canonicalMedia) && (
                <div className="eq-image-panel">
                  <p className="eq-image-label">Current Image</p>
                  <QuestionImage question={{ id: questionId, questionImageURL: originalQuestionImageURL,
                    questionMedia: canonicalMedia }} context={{ type: "question-owner" }}
                    alt="Current Question" className="eq-image" />
                </div>
              )}
              <div className="eq-upload-panel">
              <label className="eq-label" htmlFor="questionImageInput">Choose a question image</label>
              <p className="eq-help" id="eq-image-help">Select an image to add or replace the current question image. A selected image is also uploaded when you update the question.</p>
              <div className="eq-file-picker">
              <input
                id="questionImageInput"
                type="file"
                accept="image/*"
                onChange={handleQuestionFileChange}
                className="eq-file-input"
                aria-describedby="eq-image-help eq-file-name"
              />
                <label
                  htmlFor="questionImageInput"
                  className="eq-button eq-button--secondary eq-file-button"
                >
                  <ImagePlus size={17} aria-hidden="true" /> Browse Files
                </label>
                <span className="eq-file-name" id="eq-file-name" aria-live="polite">
                  {questionImage ? questionImage.name : "No new file selected"}
                </span>
              </div>
              <button
                type="button"
                onClick={uploadQuestionImage}
                disabled={!questionImage}
                className="eq-button eq-button--secondary eq-upload-button"
              >
                <Upload size={17} aria-hidden="true" /> Upload New Question Image
              </button>
              {questionImage && questionImageURL && (
                <div className="eq-image-panel eq-new-image">
                  <p className="eq-image-label">New Image Preview</p>
                  <img
                    src={questionImageURL}
                    alt="New Question Preview"
                    className="eq-image"
                  />
                </div>
              )}
            </div>
          </div>
          </section>

          <section className="eq-section" aria-labelledby="eq-details-title">
            <div className="eq-section-heading">
              <span className="eq-section-icon eq-section-icon--purple"><Settings2 size={22} aria-hidden="true" /></span>
              <div><h2 id="eq-details-title">Marks & question details</h2><p>Keep the question organised and set how students respond.</p></div>
            </div>
          <div className="eq-details-grid">
            <div>
              <label className="eq-label" htmlFor="eq-max-mark">
                Max Mark *
              </label>
              <input
                id="eq-max-mark"
                type="number"
                name="maxMark"
                placeholder="e.g. 5"
                value={question.maxMark}
                onChange={handleInputChange}
                required
                disabled={question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE}
                className="eq-input"
                aria-describedby={question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE ? "eq-mark-note" : undefined}
              />
              {question.questionType === QUESTION_TYPE_MULTIPLE_CHOICE && (
                <span className="eq-help" id="eq-mark-note">Multiple-choice questions are worth 1 mark.</span>
              )}
            </div>

            <div>
              <label className="eq-label" htmlFor="eq-topic">
                Topic *
              </label>
              <input
                id="eq-topic"
                type="text"
                name="topic"
                placeholder="e.g. Photosynthesis"
                value={question.topic}
                onChange={handleInputChange}
                required
                className="eq-input"
              />
            </div>

            <div>
              <label className="eq-label" htmlFor="eq-subject">
                Subject *
              </label>
              <input
                id="eq-subject"
                type="text"
                name="subject"
                placeholder="e.g. Biology"
                value={question.subject}
                onChange={handleInputChange}
                required
                className="eq-input"
              />
            </div>

            <div>
              <label className="eq-label" htmlFor="eq-level">
                Level *
              </label>
              <input
                id="eq-level"
                type="text"
                name="level"
                placeholder="e.g. GCSE"
                value={question.level}
                onChange={handleInputChange}
                required
                className="eq-input"
              />
            </div>

            <div>
              <label className="eq-label" htmlFor="eq-spec-code">
                Spec Code *
              </label>
              <input
                id="eq-spec-code"
                type="text"
                name="specCode"
                placeholder="e.g. AQA-BIO-4.1"
                value={question.specCode}
                onChange={handleInputChange}
                required
                className="eq-input"
              />
            </div>

            {question.questionType !== QUESTION_TYPE_MULTIPLE_CHOICE && (
              <div>
                <label className="eq-label" htmlFor="eq-answer-size">
                  Answer Input Size
                </label>
                <select
                  id="eq-answer-size"
                  name="textBoxSize"
                  value={question.textBoxSize}
                  onChange={handleInputChange}
                  className="eq-input"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
            )}
          </div>
          </section>

          <div className="eq-form-footer">
            <p className="eq-help">* Required fields</p>
            <button
              type="submit"
              disabled={isSaving || isSaved}
              className="eq-button eq-button--primary"
            >
              {isSaving ? <Loader2 size={18} className="eq-spinner" aria-hidden="true" /> : <Save size={18} aria-hidden="true" />}
              {isSaving ? "Saving..." : isSaved ? "Saved" : "Update Question"}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
};

export default EditQuestion;
